<template>
    <CardContentPanel icon="creditcard" v-bind="{action, index}">
        <template #title>{{ $t('projectclient.projectview.activity.pending.actions.PAY_PROJECT_EXPENSE.title') }}</template>
        <template #body>
            {{ getLabel }}
        </template>
        <template #actions>
            <NuxtLink
                :to="`/project-payment/${action.extraData.expenseApprovalSetId}?origin=project-view`"
                external
                data-testid="pending-action-card-cta-primary"
            >
                <VJoyButton variant="main" size="small">
                    {{ $t('projectclient.projectview.activity.pending.actions.PAY_PROJECT_EXPENSE.action.primary') }}
                </VJoyButton>
            </NuxtLink>
        </template>
    </CardContentPanel>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useProjectInformationStore} from '@common/stores';
    import {addDays} from 'date-fns';
    import {parse} from 'tinyduration';
    import {useTranslation, useFormat, useLogger} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import CardContentPanel from '../CardContentPanel.vue';
    import type {ActivityPendingActionByType} from '@/features/project-view';

    const props = defineProps<{
        action: ActivityPendingActionByType<'PAY_PROJECT_EXPENSE'>;
        index: number;
    }>();

    const projectInformationStore = useProjectInformationStore();
    const {formatDate, formatAmount} = useFormat();
    const {t} = useTranslation();
    const logger = useLogger();

    const dueFrom = computed(() => {
        if (props.action.extraData.dueFrom) {
            return formatDate(addDays(new Date(), parse(props.action.extraData.dueFrom).days ?? 0));
        }
        logger.error(
            `Missing "dueFrom" extraData from PAY_PROJECT_LOT pending action in project ${projectInformationStore.projectInformation.projectId}`,
        );
        return '-';
    });

    const formattedAmount = computed<string>(() => {
        const amount = props.action.extraData.remaining;
        return !isNaN(amount) ? formatAmount(projectInformationStore.projectInformation.contract.currency, amount, {decimals: 2}) : '-';
    });

    const getLabel = computed(() => {
        const {extraData} = props.action;
        const translationKey = `projectclient.projectview.activity.pending.actions.PAY_PROJECT_EXPENSE.description`;
        if (typeof extraData.billingContact === 'undefined') {
            return t(translationKey, [dueFrom.value, formattedAmount.value]);
        } else {
            return t(`${translationKey}.with.billing.email`, [extraData.billingContact, dueFrom.value, formattedAmount.value]);
        }
    });
</script>
